// Copyright (c) 2024-2026 Echonum LLC. All rights reserved.
// Product: Exploit Score ETS (Exploit Threat Score)
// Version: 2.0.4
//
// This file is part of Exploit Score ETS, proprietary software developed by
// Echonum LLC. Unauthorized copying, distribution, modification, or use of
// this file, via any medium, is strictly prohibited without express written
// permission from Echonum LLC.
//
// Confidential and Proprietary.

import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { User, LogOut, Settings, ChevronDown } from 'lucide-react'

export default function Header() {
  const { user, logout } = useAuth()
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <header className="h-16 bg-dark-card border-b border-dark-border flex items-center justify-end px-6">
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors"
        >
          <div className="w-8 h-8 bg-primary-500/20 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-primary-500" />
          </div>
          <div className="text-left hidden sm:block">
            <p className="text-sm font-medium">{user?.contact_name || user?.email}</p>
            {user?.company_name && (
              <p className="text-xs text-gray-400">{user.company_name}</p>
            )}
          </div>
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </button>

        {showDropdown && (
          <div className="absolute right-0 mt-2 w-56 bg-dark-card border border-dark-border rounded-lg shadow-lg py-1 z-50">
            <div className="px-4 py-3 border-b border-dark-border">
              <p className="text-sm font-medium">{user?.email}</p>
              {user?.company_name && (
                <p className="text-xs text-gray-400 mt-1">{user.company_name}</p>
              )}
            </div>
            <Link
              to="/account"
              className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-800 transition-colors"
              onClick={() => setShowDropdown(false)}
            >
              <Settings className="w-4 h-4" />
              Account Settings
            </Link>
            <button
              onClick={() => {
                setShowDropdown(false)
                logout()
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-gray-800 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        )}
      </div>
    </header>
  )
}
